import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as ordiniApi from '@/api/ordini'
import { useCambiaStatoSpedizione, useConfermaRientroSpedizione } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Truck, Package, MapPin, Calendar, ArrowLeft, ExternalLink, User, ArrowRightLeft } from 'lucide-react'
import DdtButton from '@/components/ordini/DdtButton'
import { toast } from 'sonner'

function localDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const CORRIERE_LABELS = {
  brt: 'BRT',
  gls: 'GLS',
  ups: 'UPS',
  dhl: 'DHL',
  fedex: 'FedEx',
  sda: 'SDA',
  tnt: 'TNT',
  poste: 'Poste Italiane',
  colauri: 'COLAURI',
  ritiro_in_sede: 'RITIRO IN SEDE',
  altro: 'Altro',
}

const STATO_COLORS = {
  preparazione: 'bg-yellow-500',
  pronto: 'bg-orange-500',
  ritirato: 'bg-blue-500',
  in_transito: 'bg-purple-500',
  in_consegna: 'bg-indigo-500',
  consegnato: 'bg-green-500',
  problema: 'bg-red-500',
  reso: 'bg-red-500',
}

const STATI_SELEZIONABILI = [
  'preparazione',
  'pronto',
  'ritirato',
  'in_transito',
  'in_consegna',
  'consegnato',
  'problema',
]

const formatStatoLabel = (stato) =>
  stato.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

export default function SpedizioneDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const cambiaStatoMutation = useCambiaStatoSpedizione()
  const confermaRientroMutation = useConfermaRientroSpedizione()
  const [cambiaStatoKey, setCambiaStatoKey] = useState(0)
  const [editingDate, setEditingDate] = useState(false)
  const [dateValue, setDateValue] = useState('')
  const [savingDate, setSavingDate] = useState(false)

  const { data: spedizione, isLoading } = useQuery({
    queryKey: ['spedizione', id],
    queryFn: () => ordiniApi.getSpedizione(id).then(res => res.data),
  })

  const handleCambiaStato = (nuovoStato) => {
    // Resetta il Select "Cambia stato" al placeholder dopo l'interazione.
    setCambiaStatoKey(k => k + 1)
    cambiaStatoMutation.mutate({ id, stato: nuovoStato })
  }

  const handleConfermaRientro = () => {
    const causale = window.prompt('Confermi che tutti i colli sono fisicamente rientrati? Indica la causale del rientro:')
    if (!causale?.trim()) return
    if (!window.confirm('La merce rientrerà in magazzino e sarà riservata per questo ordine. Confermi?')) return
    confermaRientroMutation.mutate({ id, causale: causale.trim() })
  }

  const handleSaveDate = async () => {
    const date = new Date(dateValue)
    if (!dateValue || Number.isNaN(date.getTime())) {
      toast.error('Indica una data e un’ora di spedizione valide.')
      return
    }
    setSavingDate(true)
    try {
      await ordiniApi.updateSpedizione(id, { data_spedizione: date.toISOString() })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['spedizione', id] }),
        queryClient.invalidateQueries({ queryKey: ['spedizioni'] }),
        queryClient.invalidateQueries({ queryKey: ['ordine'] }),
      ])
      setEditingDate(false)
      toast.success('Data di spedizione aggiornata')
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile aggiornare la data di spedizione.')
    } finally {
      setSavingDate(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Caricamento...</div>
  }

  if (!spedizione) {
    return <div className="text-center py-8">Spedizione non trovata</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/ordini/spedizioni')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alla Lista
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              Spedizione {spedizione.codice || `#${spedizione.id}`}
            </h1>
            <p className="text-muted-foreground">
              Cliente: {spedizione.cliente_nome} · Ordine:
              <button
                onClick={() => navigate(`/ordini/${spedizione.ordine}`)}
                className="ml-2 font-mono font-medium text-primary hover:underline"
              >
                {spedizione.ordine_numero}
              </button>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DdtButton spedizione={spedizione} />
          <Badge className={`${STATO_COLORS[spedizione.stato] || 'bg-gray-500'} text-white text-lg px-4 py-2`}>
            {spedizione.stato_display}
          </Badge>
        </div>
      </div>

      {/* Azioni Stato */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Azioni
          </CardTitle>
        </CardHeader>
        <CardContent className="flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Cambia stato:</span>
            <Select
              key={cambiaStatoKey}
              onValueChange={(value) => handleCambiaStato(value)}
              disabled={cambiaStatoMutation.isPending || spedizione.stato === 'reso'}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Seleziona stato..." />
              </SelectTrigger>
              <SelectContent>
                {STATI_SELEZIONABILI.map((stato) => (
                  <SelectItem key={stato} value={stato}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATO_COLORS[stato]}`} />
                      <span>{formatStatoLabel(stato)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {spedizione.stato === 'problema' && (
            <Button onClick={handleConfermaRientro} disabled={confermaRientroMutation.isPending}>
              Conferma rientro fisico
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Dati Spedizione */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Dati Spedizione
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Corriere</label>
              <div className="mt-1">
                <Badge variant="outline" className="text-base">
                  {CORRIERE_LABELS[spedizione.corriere] || spedizione.corriere}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tracking Number</label>
              <div className="mt-1 flex items-center gap-2">
                {spedizione.tracking_number ? (
                  <>
                    <span className="font-mono font-medium">{spedizione.tracking_number}</span>
                    {spedizione.tracking_url && (
                      <a
                        href={spedizione.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Data Spedizione</label>
              {editingDate ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <Input
                    type="datetime-local"
                    aria-label="Data e ora di spedizione"
                    className="w-auto"
                    value={dateValue}
                    onChange={(event) => setDateValue(event.target.value)}
                  />
                  <Button size="sm" onClick={handleSaveDate} disabled={savingDate || !dateValue}>Salva</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingDate(false)} disabled={savingDate}>Annulla</Button>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDate(spedizione.data_spedizione)}</span>
                  {!spedizione.ddt_numero && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDateValue(localDateTime(spedizione.data_spedizione))
                        setEditingDate(true)
                      }}
                    >
                      {spedizione.data_spedizione ? 'Modifica' : 'Inserisci'}
                    </Button>
                  )}
                </div>
              )}
              {spedizione.ddt_numero && (
                <p className="text-xs text-muted-foreground mt-1">Data fissata nel DDT emesso.</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Data Consegna Prevista</label>
              <div className="mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{formatDate(spedizione.data_consegna_prevista)}</span>
              </div>
            </div>
            {spedizione.data_consegna_effettiva && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Data Consegna Effettiva</label>
                <div className="mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDate(spedizione.data_consegna_effettiva)}</span>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Peso Totale</label>
              <div className="mt-1">
                <span className="font-medium">{spedizione.peso_totale_kg || 0} kg</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Costo Spedizione</label>
              <div className="mt-1">
                <span className="font-medium">{formatCurrency(spedizione.costo_spedizione)}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Numero Colli</label>
              <div className="mt-1 flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{spedizione.numero_colli || 0}</span>
              </div>
            </div>
          </div>
          {(spedizione.note || spedizione.ordine_note || spedizione.ordine_note_interne) && (
            <div className="mt-6 space-y-4">
              {spedizione.note && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Note spedizione</label>
                  <p className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">{spedizione.note}</p>
                </div>
              )}
              {spedizione.ordine_note && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Note cliente dell’ordine</label>
                  <p className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">{spedizione.ordine_note}</p>
                </div>
              )}
              {spedizione.ordine_note_interne && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Note interne dell’ordine</label>
                  <p className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">{spedizione.ordine_note_interne}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Indirizzo Destinazione */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {spedizione.corriere === 'ritiro_in_sede' ? 'Indirizzo di Ritiro' : 'Indirizzo Destinazione'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {spedizione.indirizzo_presso && (
              <div className="text-sm text-muted-foreground">
                c/o {spedizione.indirizzo_presso}
              </div>
            )}
            <div className="font-medium">
              {spedizione.indirizzo_via} {spedizione.indirizzo_civico}
            </div>
            <div>
              {spedizione.indirizzo_cap} {spedizione.indirizzo_citta} ({spedizione.indirizzo_provincia})
            </div>
            <div className="text-sm text-muted-foreground">
              {spedizione.indirizzo_nazione || 'Italia'}
            </div>
            {spedizione.indirizzo_note && (
              <div className="mt-3 p-3 bg-muted rounded-md">
                <p className="text-sm">{spedizione.indirizzo_note}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Colli */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Colli ({spedizione.colli?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {spedizione.colli?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nessun collo trovato
            </div>
          ) : (
            <div className="space-y-4">
              {spedizione.colli?.map((collo) => (
                <Card key={collo.id} className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        Collo {collo.numero_collo_display}
                        {collo.codice_collo && (
                          <span className="ml-2 text-sm font-mono text-muted-foreground">
                            {collo.codice_collo}
                          </span>
                        )}
                      </CardTitle>
                      {collo.destinatario && (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{collo.destinatario_nome}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 mb-4 sm:grid-cols-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Peso</label>
                        <div className="font-medium">{collo.peso_kg} kg</div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Dimensioni</label>
                        <div className="text-sm">
                          {collo.lunghezza_cm && collo.larghezza_cm && collo.altezza_cm
                            ? `${collo.lunghezza_cm}×${collo.larghezza_cm}×${collo.altezza_cm} cm`
                            : '-'}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Valore</label>
                        <div className="font-medium">{formatCurrency(collo.valore_dichiarato)}</div>
                      </div>
                    </div>
                    {collo.righe_ordine?.length > 0 && (
                      <div>
                        <label className="text-xs text-muted-foreground mb-2 block">
                          Contenuto ({collo.righe_ordine.length})
                        </label>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Articolo</TableHead>
                              <TableHead>Codice</TableHead>
                              <TableHead className="text-right">Quantità</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {collo.righe_ordine.map((assegnazione) => (
                              <TableRow key={assegnazione.id}>
                                <TableCell>{assegnazione.riga_ordine_descrizione}</TableCell>
                                <TableCell className="font-mono text-xs">
                                  {assegnazione.riga_ordine_codice}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {assegnazione.quantita}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    {collo.note && (
                      <div className="mt-4">
                        <label className="text-xs text-muted-foreground">Note collo</label>
                        <p className="mt-1 text-sm whitespace-pre-wrap">{collo.note}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
